import { Box, Select, createListCollection, Table, Mark, Text, Alert, type ListCollection, Flex } from '@chakra-ui/react';
import { memo, useCallback, useMemo, type Dispatch, type SetStateAction } from 'react';
import { type FileData, type FormValues } from './ImportContacts';
import { FormSwitch, useMobileContext, groupBy } from '@shared';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useGetAllCustomFields } from '../../../../api/hooks/useCustomField';
import CustomFieldDrawer from '../customFields/CustomFieldDrawer';
import { useDrawer } from '@admin';

type Props = {
	fileData: FileData | null;
	columnMappings: Record<string, string> | null;
	setColumnMappings: Dispatch<SetStateAction<Record<string, string> | null>>;
	handleStepReady: (ready?: boolean, goNext?: boolean) => void;
	steps: Array<{
		id: string;
		ready: boolean;
		index: number;
	}>;
};

export const MapColumns = memo(({ fileData, columnMappings, handleStepReady, setColumnMappings, steps }: Props) => {
	const { t } = useTranslation();
	const { isMobile } = useMobileContext();
	const isFirstRowHeader = useWatch<FormValues>({ name: 'isFirstRowHeader' });
	const firstColumnIndex = useWatch<FormValues>({ name: 'firstColIndex' });
	const { data } = useGetAllCustomFields();
	const customFields = useMemo(() => data?.data?.customFields ?? [], [data]);

	const columns = useMemo(
		() => [
			{ name: 'phone', label: t('general.phone'), required: true },
			{ name: 'email', label: t('general.email') },
			{ name: 'firstName', label: t('general.first_name') },
			{ name: 'lastName', label: t('general.last_name') },
			{ name: 'company', label: t('general.company') },
			{ name: 'jobTitle', label: t('general.job_title') },
			{ name: 'city', label: t('general.city') },
			{ name: 'postalCode', label: t('general.postal_code') },
			{ name: 'postalAddress', label: t('general.postal_address') },
			{ name: 'description', label: t('general.description') },
			{ name: 'newCustomField', label: t('general.add_new_custom_field'), category: t('general.custom_fields'), required: false },
			...customFields.map(cf => ({ name: `contactCustomFields.customFieldID-${cf.customFieldID}`, label: cf.label, category: t('general.custom_fields'), required: false })),
		],
		[t, customFields],
	);

	const colCollection = useMemo(() => {
		return createListCollection({
			items: [
				{ value: 'ignore', label: t('general.ignore'), category: '' },
				...columns.map(item => ({
					value: item.name,
					label: item.label,
					category: 'category' in item ? item.category : t('general.general_fields'),
					// eslint-disable-next-line max-nested-callbacks
					disabled: Object.values(columnMappings ?? {}).some(v => v && v?.split('.')?.[1] === item.name),
				})),
			],
		});
	}, [columnMappings, columns, t]);

	const requiredColumnsSet = useCallback(
		(colMappings: Record<string, string>) => {
			const requiredCols = columns.filter(col => col.required);
			const mappedColMappings = Object.values(colMappings ?? {}).map(cm => cm?.split('.')[1]);
			const allRequiredPresent = requiredCols.every(col => mappedColMappings.includes(col.name));
			handleStepReady(allRequiredPresent);
		},
		[handleStepReady, columns],
	);

	return (
		<Box>
			{!steps[1]?.ready && (
				<Alert.Root mb={4}>
					<Alert.Indicator />
					<Alert.Content>
						<Alert.Title>{t('contacts.phone_number_is_needed_to_continue')}</Alert.Title>
					</Alert.Content>
				</Alert.Root>
			)}
			<Table.Root striped>
				{!isMobile && (
					<Table.Header>
						<Table.Row>
							<Table.ColumnHeader>{t('general.map_to_column')}</Table.ColumnHeader>
							<Table.ColumnHeader>{t('general.column_data')}</Table.ColumnHeader>
						</Table.Row>
					</Table.Header>
				)}
				<Table.Body>
					{fileData?.previewData?.map((dataRow, index) => {
						const rowIndex = index + 1 + Number(firstColumnIndex);
						const value = columnMappings?.[rowIndex];

						if (isMobile)
							return (
								<Table.Row key={index}>
									<Table.Cell padding={'1.125rem'}>
										<Text fontWeight={600}>{t('general.column_data')}</Text>
										<Text lineClamp={1} my={1}>
											{isFirstRowHeader ? (
												<Mark textDecor="line-through" variant="solid" color="black" backgroundColor={'transparent'}>
													{dataRow[0]}
												</Mark>
											) : (
												dataRow[0]
											)}
											{dataRow.slice(1, 10).map((text, ind) => {
												const maxCharactersPerCell = 30;
												return <span key={ind}>, {text?.toString()?.slice(0, maxCharactersPerCell)}</span>;
											})}
										</Text>
										<MappingSelect
											value={value}
											rowIndex={rowIndex}
											colCollection={colCollection}
											requiredColumnsSet={requiredColumnsSet}
											setColumnMappings={setColumnMappings}
										/>
									</Table.Cell>
								</Table.Row>
							);

						return (
							<Table.Row key={index}>
								<Table.Cell minW="150px">
									<MappingSelect
										value={value}
										rowIndex={rowIndex}
										colCollection={colCollection}
										requiredColumnsSet={requiredColumnsSet}
										setColumnMappings={setColumnMappings}
									/>
								</Table.Cell>
								<Table.Cell>
									<Text lineClamp={1}>
										{isFirstRowHeader ? (
											<Mark textDecor="line-through" variant="solid" color="black" backgroundColor={'transparent'}>
												{dataRow[0]}
											</Mark>
										) : (
											dataRow[0]
										)}
										{dataRow.slice(1, 10).map((text, i) => {
											const maxCharactersPerCell = 30;
											return <span key={i}>, {text?.toString()?.slice(0, maxCharactersPerCell)}</span>;
										})}
									</Text>
								</Table.Cell>
							</Table.Row>
						);
					})}
				</Table.Body>
			</Table.Root>
			<Box mt={4} px={3}>
				<FormSwitch label={t('contacts.is_first_row_header')} helperText={t('contacts.is_first_row_header_desc')} name="isFirstRowHeader" />
			</Box>
		</Box>
	);
});

type MappingSelectProps = {
	value: string | undefined;
	rowIndex: number;
	setColumnMappings: Dispatch<SetStateAction<Record<string, string> | null>>;
	colCollection: ListCollection<
		| {
				value: string;
				label: string;
				category: string;
				disabled: boolean;
		  }
		| {
				value: string;
				label: string;
				category: string;
		  }
	>;
	requiredColumnsSet: (colMappings: Record<string, string>) => void;
};

const MappingSelect = memo(({ value, rowIndex, colCollection, requiredColumnsSet, setColumnMappings }: MappingSelectProps) => {
	const valueKey = value?.includes('contactCustomFields') ? value : value?.split('.')[1] || null;
	const columnsByCategory = Object.entries(groupBy(colCollection.items, item => item.category));
	const { t } = useTranslation();

	const { openDrawer } = useDrawer();

	return (
		<Flex flexDir="column">
			<Select.Root
				lazyMount
				size="sm"
				collection={colCollection}
				value={valueKey ? [valueKey] : ['ignore']}
				onValueChange={e => {
					const fieldValue = e.value[0];

					if (fieldValue === 'newCustomField') {
						openDrawer({
							id: 'customFieldDrawer',
							content: (
								<CustomFieldDrawer
									onCreated={(customField: CustomField) => {
										setColumnMappings(prev => {
											const updated = { ...prev, [rowIndex]: `contactCustomFields.customFieldID-${customField.customFieldID}` };
											requiredColumnsSet(updated);
											return updated;
										});
									}}
								/>
							),
						});
						return;
					}

					if (!fieldValue || fieldValue === 'ignore') {
						setColumnMappings(prev => {
							const updated = { ...prev, [rowIndex]: null };
							requiredColumnsSet(updated);
							return updated;
						});
					} else if (fieldValue.includes('contactCustomFields')) {
						setColumnMappings(prev => {
							const updated = { ...prev, [rowIndex]: fieldValue };
							requiredColumnsSet(updated);
							return updated;
						});
					} else {
						setColumnMappings(prev => {
							const updated = { ...prev, [rowIndex]: `contacts.${fieldValue}` };
							requiredColumnsSet(updated);
							return updated;
						});
					}
				}}
			>
				<Select.HiddenSelect />
				<Select.Control>
					<Select.Trigger>
						<Select.ValueText color={valueKey ? 'black' : 'fg.muted'} />
					</Select.Trigger>
					<Select.IndicatorGroup>
						<Select.Indicator />
					</Select.IndicatorGroup>
				</Select.Control>

				<Select.Positioner>
					<Select.Content maxHeight={'200px'}>
						{columnsByCategory.map(([category, items]) => (
							<Select.ItemGroup key={category}>
								{category && <Select.ItemGroupLabel color="blue.700">{category}</Select.ItemGroupLabel>}
								{items.map(item => (
									<Select.Item item={item} key={item.value} textDecoration={item.value === 'newCustomField' ? 'underline' : 'non2'}>
										{item.label}
										<Select.ItemIndicator />
									</Select.Item>
								))}
							</Select.ItemGroup>
						))}
					</Select.Content>
				</Select.Positioner>
			</Select.Root>
			<Text fontSize={'13px'}>{valueKey === 'email' ? t('general.non_valid_email_will_be_skipped') : null}</Text>
		</Flex>
	);
});
